// Presentation → "Make virtuals" (CEO 2026-10-10): tick items, pick colours,
// give the client's logo once (saved to their logo library), make virtuals,
// then review them in the grid. Approved virtuals attach to their item.
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, ImagePlus, Loader2, Sparkles, Wand2, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { LogoPrepCard, SpendMeter, useVirtualSet, VirtualsWorkspace } from "./VirtualsWorkspace";
import { errorText, specLine, virtualsApi } from "./virtualsApi";

interface ClientLogo { id: string; fileId: string; filename: string; url: string | null }

const VISION_CENTS = 2;
const POLISH_CENTS = 4;

export function MakeVirtualsDialog(props: { presentationId: string; onClose: () => void; initialSetId?: string | null }) {
  const qc = useQueryClient();
  const [setId, setSetId] = useState<string | null>(props.initialSetId ?? null);
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [logoFileId, setLogoFileId] = useState<string | null>(null);
  const [polish, setPolish] = useState(true);
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [step, setStep] = useState<"pick" | "review">(props.initialSetId ? "review" : "pick");
  const fileRef = useRef<HTMLInputElement>(null);

  const options = useQuery({ queryKey: ["virtual-options", props.presentationId], queryFn: () => virtualsApi.presentationOptions(props.presentationId) });
  const logos = useQuery({ queryKey: ["client-logos", props.presentationId], queryFn: () => api<{ rows: ClientLogo[] }>(`/presentations/${props.presentationId}/client-logos`).then((r) => r.rows) });
  const setQ = useVirtualSet(setId);

  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && !working && props.onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [props, working]);

  const count = Object.values(picked).reduce((n, c) => n + c.length, 0);
  const estimate = count * (VISION_CENTS + (polish ? POLISH_CENTS : 0));
  const toggleItem = (itemId: string, firstColour: string | undefined) =>
    setPicked((p) => {
      const n = { ...p };
      if (n[itemId]) delete n[itemId];
      else n[itemId] = firstColour ? [firstColour] : [];
      return n;
    });
  const toggleColour = (itemId: string, colour: string) =>
    setPicked((p) => {
      const cur = p[itemId] ?? [];
      const next = cur.includes(colour) ? cur.filter((c) => c !== colour) : [...cur, colour];
      const n = { ...p };
      if (next.length) n[itemId] = next;
      else delete n[itemId];
      return n;
    });

  const make = async (upload?: File) => {
    setError(null);
    setErrors([]);
    try {
      let sid = setId;
      if (upload) {
        setWorking("Preparing the logo…");
        const s = await virtualsApi.uploadLogo(upload, { presentationId: props.presentationId });
        sid = s.id;
        setLogoFileId(s.logoFileId);
        await qc.invalidateQueries({ queryKey: ["client-logos", props.presentationId] });
      } else if (logoFileId && (!setQ.data || setQ.data.logoFileId !== logoFileId)) {
        setWorking("Preparing the logo…");
        sid = (await virtualsApi.setFromFile(logoFileId, { presentationId: props.presentationId })).id;
      }
      if (!sid) throw new Error("Choose the client's logo first.");
      setSetId(sid);
      setWorking(`Making ${count} virtual${count === 1 ? "" : "s"}… about ${Math.max(5, count * (polish ? 15 : 6))} seconds`);
      const targets = Object.entries(picked).map(([itemId, colours]) => ({ kind: "item" as const, presentationItemId: itemId, colours }));
      const r = await virtualsApi.generate(sid, targets, polish);
      setErrors(r.errors.map((e) => `${e.productName}${e.colour ? ` (${e.colour})` : ""}: ${e.message}`));
      await qc.invalidateQueries({ queryKey: ["virtual-set", sid] });
      await qc.invalidateQueries({ queryKey: ["virtuals-spend"] });
      setStep("review");
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Make virtuals">
      <div className="flex w-full max-w-6xl flex-col overflow-hidden bg-canvas sm:rounded-lg">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule bg-white px-5 py-3">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Presentation / Virtuals</p>
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">{step === "pick" ? "Make virtuals" : "Review virtuals"}</h2>
          </div>
          <div className="flex items-center gap-4">
            <SpendMeter />
            {step === "review" && <Button variant="outline" onClick={() => setStep("pick")}>Make more</Button>}
            <button type="button" onClick={props.onClose} disabled={!!working} aria-label="Close" className="rounded p-1.5 text-muted hover:bg-cream-100 hover:text-ink"><X className="h-5 w-5" /></button>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-5">
          {step === "review" && setQ.data ? (
            <div className="space-y-5">
              <LogoPrepCard set={setQ.data} />
              {errors.length > 0 && <ErrorList errors={errors} />}
              <VirtualsWorkspace set={setQ.data} onChanged={() => qc.invalidateQueries({ queryKey: ["presentation-virtuals", props.presentationId] })} emptyHint="No virtuals in this set yet — Make more." />
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
              <section className="space-y-3" aria-label="Products">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-semibold text-ink">1. Tick products and colors</h3>
                  <span className="text-xs text-muted">{count} virtual{count === 1 ? "" : "s"}</span>
                </div>
                {options.isLoading && <p className="text-sm text-muted">Loading products…</p>}
                <ul className="space-y-2">
                  {(options.data ?? []).map((it) => {
                    const on = !!picked[it.itemId];
                    const disabled = !!it.problem;
                    return (
                      <li key={it.itemId} className={cn("rounded-lg border bg-white p-3", on ? "border-ink" : "border-rule", disabled && "opacity-60")}>
                        <label className="flex cursor-pointer items-start gap-3">
                          <input type="checkbox" checked={on} disabled={disabled} onChange={() => toggleItem(it.itemId, it.colours[0]?.colour)} className="mt-1 h-4 w-4 accent-ink" />
                          <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-md border border-rule bg-canvas">
                            {it.colours[0] && <img src={it.colours[0].photoUrl} alt="" className="h-full w-full object-contain" loading="lazy" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-ink">{it.name}</p>
                            <p className={cn("text-xs", it.spec?.estimated ? "text-warn" : "text-muted")}>{it.problem ?? specLine(it.spec)}{it.spec?.estimated && !it.problem ? " — size will be estimated" : ""}</p>
                          </div>
                        </label>
                        {on && it.colours.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5 pl-[5.25rem]" role="group" aria-label={`Colors for ${it.name}`}>
                            {it.colours.map((c) => {
                              const sel = picked[it.itemId]?.includes(c.colour);
                              return (
                                <button key={c.colour} type="button" onClick={() => toggleColour(it.itemId, c.colour)} aria-pressed={sel} className={cn("inline-flex h-8 items-center gap-1 rounded-md border px-2.5 text-xs", sel ? "border-ink bg-ink text-white" : "border-rule bg-white text-ink hover:bg-cream-100")}>
                                  {sel && <Check className="h-3 w-3" aria-hidden />}{c.colour}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>

              <aside className="space-y-4" aria-label="Logo and options">
                <div className="rounded-lg border border-rule bg-white p-4">
                  <h3 className="mb-1 text-base font-semibold text-ink">2. The client's logo</h3>
                  <p className="mb-3 text-xs text-muted">Saved to the client's logo library for next time.</p>
                  <div className="grid grid-cols-3 gap-2">
                    {(logos.data ?? []).slice(0, 8).map((l) => (
                      <button key={l.id} type="button" onClick={() => setLogoFileId(l.fileId)} aria-pressed={logoFileId === l.fileId} title={l.filename} className={cn("flex aspect-square items-center justify-center overflow-hidden rounded-md border p-1.5", logoFileId === l.fileId ? "border-ink ring-1 ring-ink" : "border-rule hover:border-ink/40")}>
                        {l.url ? <img src={l.url} alt={l.filename} className="max-h-full max-w-full object-contain" /> : <span className="truncate text-[10px] text-muted">{l.filename}</span>}
                      </button>
                    ))}
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={!count || !!working} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-md border border-dashed border-rule text-xs text-muted hover:border-ink hover:text-ink disabled:opacity-50" title={count ? "Upload and make" : "Tick a product first"}>
                      <ImagePlus className="h-5 w-5" aria-hidden /> Upload
                    </button>
                  </div>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif,application/pdf,application/postscript,application/illustrator,.ai,.eps,.pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void make(f); }} />
                  <p className="mt-2 text-[11px] text-muted">PNG, JPG, SVG, PDF, AI or EPS. We remove the background and count the colors.</p>
                </div>

                <div className="rounded-lg border border-rule bg-white p-4">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input type="checkbox" checked={polish} onChange={(e) => setPolish(e.target.checked)} className="mt-1 h-4 w-4 accent-ink" />
                    <span>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-ink"><Sparkles className="h-4 w-4" aria-hidden /> AI polish</span>
                      <span className="block text-xs text-muted">Adds curve, texture and light. Kept only if the logo comes back unchanged — the exact virtual is always there.</span>
                    </span>
                  </label>
                  <p className="mt-3 text-xs text-muted">Estimated AI cost: <span className="tabular-nums text-ink">${(estimate / 100).toFixed(2)}</span></p>
                </div>

                {error && <p className="rounded-lg border border-risk/20 bg-risk-bg px-3 py-2 text-sm text-risk" role="alert">{error}</p>}
                <Button className="w-full" size="lg" disabled={!count || (!logoFileId && !setId) || !!working} onClick={() => void make()}>
                  {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {working ? "Working…" : `Make ${count || ""} virtual${count === 1 ? "" : "s"}`}
                </Button>
                {working && <p className="text-center text-xs text-muted" role="status">{working}</p>}
              </aside>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ErrorList({ errors }: { errors: string[] }) {
  return (
    <div className="rounded-lg border border-warn/30 bg-warn-bg px-4 py-3 text-sm text-warn" role="alert">
      <p className="mb-1 flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" aria-hidden /> Some virtuals couldn't be made</p>
      <ul className="list-disc pl-5">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
    </div>
  );
}

// ── Approved virtuals on the presentation's item cards ─────────────────────
type Approved = Array<{ itemId: string; virtualId: string; colour: string | null; imageUrl: string }>;
const PresentationVirtualsCtx = createContext<{ approved: Approved; open: () => void } | null>(null);

export function PresentationVirtualsProvider(props: { presentationId: string; open: () => void; children: React.ReactNode }) {
  const q = useQuery({ queryKey: ["presentation-virtuals", props.presentationId], queryFn: () => virtualsApi.presentationVirtuals(props.presentationId), retry: false });
  const value = useMemo(() => ({ approved: q.data?.approved ?? [], open: props.open }), [q.data, props.open]);
  return <PresentationVirtualsCtx.Provider value={value}>{props.children}</PresentationVirtualsCtx.Provider>;
}

export function usePresentationLatestSet(presentationId: string) {
  return useQuery({ queryKey: ["presentation-virtuals", presentationId], queryFn: () => virtualsApi.presentationVirtuals(presentationId), retry: false });
}

/** A row of approved virtuals under an item card (rep view only). */
export function ItemVirtualsStrip({ itemId }: { itemId: string }) {
  const ctx = useContext(PresentationVirtualsCtx);
  const mine = (ctx?.approved ?? []).filter((a) => a.itemId === itemId);
  if (!mine.length) return null;
  return (
    <div className="flex items-center gap-2 border-t border-brown-50 px-3 py-2">
      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">Virtuals</span>
      {mine.slice(0, 6).map((a) => (
        <button key={a.virtualId} type="button" onClick={ctx?.open} title={a.colour ?? "Virtual"} className="h-10 w-10 overflow-hidden rounded border border-rule bg-canvas">
          <img src={a.imageUrl} alt={`Approved virtual${a.colour ? ` — ${a.colour}` : ""}`} className="h-full w-full object-contain" />
        </button>
      ))}
      <span className="text-[11px] text-muted">Approved · shown to the client once sending virtuals is switched on</span>
    </div>
  );
}
